import PageTitle from '../components/common/PageTitle';
import { Link } from 'react-router-dom';
import { Download, Gift, CheckCircle, HelpCircle, File, AlertCircle } from 'lucide-react';

const DonacijaPage = () => {
  return (
    <>
      <PageTitle 
        title="Donacija dohodnine" 
        subtitle="Podprite naše delo in nam podarite del vaše dohodnine"
        backgroundImage="/images/donation-hero.jpg"
      />

      <section className="page-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-title text-secondary">Podarite nam 1% dohodnine</h2>
            <p className="mt-6 text-lg text-gray-700">
              Vsak davčni zavezanec lahko nameni do 1% svoje dohodnine za delovanje prostovoljnih organizacij, 
              ki opravljajo javno koristno dejavnost. Ta znesek lahko namenite našemu gasilskemu društvu, 
              saj gre za sredstva, ki bi jih sicer pustili v državnem proračunu.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="bg-primary/10 text-primary text-lg font-medium px-6 py-3 rounded-lg inline-flex items-center">
                <Gift className="mr-2 h-5 w-5" />
                Vaša donacija je za vas brezplačna, za nas pa neprecenljiva!
              </div>
            </div>
          </div>

          {/* Process explanation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 my-16">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <File className="text-primary h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">1. Obrazec</h3>
              <p className="text-gray-600 mb-4">
                Izpolnite obrazec za namenitev dela dohodnine. Obrazec lahko prenesete spodaj ali ga dobite na naši lokaciji.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <HelpCircle className="text-primary h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">2. Podatki</h3>
              <p className="text-gray-600 mb-4">
                Vpišete svoje podatke in našo davčno številko: <span className="font-semibold">12345678</span> ter ime: 
                <span className="font-semibold"> PROSTOVOLJNO GASILSKO DRUŠTVO ZGORNJA POLSKAVA</span>
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="text-primary h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">3. Oddaja</h3>
              <p className="text-gray-600 mb-4">
                Izpolnjen obrazec oddate na pristojni finančni urad ali pa ga oddate preko spletnega portala eDavki.
              </p>
            </div>
          </div>

          {/* Download section */}
          <div className="bg-gray-50 p-8 rounded-xl shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-secondary mb-4">Prenesite obrazec</h3>
                <p className="text-gray-700 mb-6">
                  Tukaj lahko prenesete obrazec za namenitev dela dohodnine za donacije (Obrazec MF-FURS obr. doh), 
                  ki ga izpolnite in oddate na vaš pristojni finančni urad ali preko portala eDavki.
                </p>
                <a 
                  href="#" 
                  className="btn-primary inline-flex"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Prenesi obrazec (PDF)
                </a>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-lg mb-3">Podatki za izpolnitev obrazca</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Naziv upravičenca:</p>
                    <p className="font-medium">PROSTOVOLJNO GASILSKO DRUŠTVO ZGORNJA POLSKAVA</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Davčna številka:</p>
                    <p className="font-medium">12345678</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Odstotek (%):</p>
                    <p className="font-medium">1,0 %</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important notes */}
          <div className="mt-12">
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg mb-6">
              <div className="flex">
                <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mr-3" />
                <div>
                  <h4 className="text-orange-800 font-medium">Pomembno</h4>
                  <p className="text-orange-700">
                    Namenitev dela dohodnine ne pomeni, da boste plačali več davka. Gre za sredstva, ki vam jih država že 
                    obračuna, vi pa se lahko odločite, da jih namesto v državni proračun namenite za podporo delovanja 
                    organizacij, kot je naše gasilsko društvo.
                  </p>
                </div>
              </div>
            </div>

            {/* Updated FAQ section with card design */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h3 className="text-xl font-bold text-secondary mb-6 border-b pb-4">Pogosta vprašanja</h3>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300">
                  <h4 className="font-semibold text-lg text-primary mb-2">Ali moram vsako leto izpolniti nov obrazec?</h4>
                  <p className="text-gray-700">
                    Ne, vaša odločitev velja do preklica. Ko enkrat oddate obrazec, bo vaša dohodnina vsako leto namenjena 
                    izbranemu upravičencu, dokler ne spremenite svoje odločitve.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300">
                  <h4 className="font-semibold text-lg text-primary mb-2">Do kdaj lahko oddam obrazec?</h4>
                  <p className="text-gray-700">
                    Obrazec lahko oddate kadarkoli med letom, upošteval pa se bo pri odmeri dohodnine za leto, v katerem je 
                    bila vloga oddana. Priporočamo, da obrazec oddate do konca leta.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300">
                  <h4 className="font-semibold text-lg text-primary mb-2">Ali lahko namenim svoj del dohodnine več organizacijam?</h4>
                  <p className="text-gray-700">
                    Da, svoj del dohodnine (skupaj do 1%) lahko razdelite med več organizacij. Na obrazcu navedete vse 
                    izbrane upravičence in odstotek, ki ga namenjate vsakemu od njih.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Hvala za vašo podporo</h2>
          <p className="text-lg max-w-3xl mx-auto mb-8">
            Z vašo pomočjo lahko še naprej zagotavljamo visoko raven požarne varnosti in 
            učinkovito pomagamo v primeru nesreč v naši lokalni skupnosti.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/kontakt" className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg inline-flex items-center transition-colors">
              Kontaktirajte nas
            </Link>
            <Link to="/oprema" className="border-2 border-white text-white hover:bg-white/10 font-semibold py-3 px-8 rounded-lg inline-flex items-center transition-colors">
              Oglejte si našo opremo
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DonacijaPage;